import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc, collection, query, where, getDocs, orderBy } from 'firebase/firestore';
import { db } from '../utils/firebase';
import { useAuth } from '../utils/firebase';
import Header from '../components/Header';
import { subDays } from 'date-fns';

const VenueProfile = () => {
  const { id: venueId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [venue, setVenue] = useState(null);
  const [games, setGames] = useState([]);
  const [analytics, setAnalytics] = useState({
    totalGames: 0,
    totalTeams: 0,
    averageTeamsPerGame: 0,
    averageGPAScore: 0,
    totalRevenue: 0,
    gamesThisMonth: 0,
    gamesThisWeek: 0
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchVenueData = useCallback(async () => {
    try {
      setLoading(true);
      
      // Fetch venue details
      const venueDoc = await getDoc(doc(db, 'venues', venueId));
      if (!venueDoc.exists()) {
        setError('Venue not found');
        setLoading(false);
        return;
      }
      
      const venueData = venueDoc.data();
      setVenue({ id: venueDoc.id, ...venueData });

      // Fetch games for this venue
      const gamesQuery = query(
        collection(db, 'games'),
        where('venueId', '==', venueId),
        orderBy('date', 'desc')
      );
      
      const gamesSnapshot = await getDocs(gamesQuery);
      const gamesData = gamesSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      setGames(gamesData);

      // Calculate analytics
      const totalGames = gamesData.length;
      const totalTeams = gamesData.reduce((sum, game) => sum + (game.teamCount || 0), 0);
      const averageTeamsPerGame = totalGames > 0 ? (totalTeams / totalGames).toFixed(1) : 0;
      
      // Calculate GPA (assuming it's stored in game results)
      const gamesWithGPA = gamesData.filter(game => game.finalResults);
      const totalGPA = gamesWithGPA.reduce((sum, game) => {
        const gameGPA = game.finalResults?.teams?.reduce((gameSum, team) => gameSum + (team.gpa || 0), 0) || 0;
        return sum + gameGPA;
      }, 0);
      const averageGPAScore = gamesWithGPA.length > 0 ? (totalGPA / gamesWithGPA.length).toFixed(2) : 0;

      // Calculate revenue (assuming $10 per game launch)
      const totalRevenue = totalGames * 10;

      // Calculate recent activity
      const now = new Date();
      const oneWeekAgo = subDays(now, 7);
      const oneMonthAgo = subDays(now, 30);
      
      const gamesThisWeek = gamesData.filter(game => {
        const gameDate = game.date ? new Date(game.date) : null;
        return gameDate && gameDate >= oneWeekAgo;
      }).length;

      const gamesThisMonth = gamesData.filter(game => {
        const gameDate = game.date ? new Date(game.date) : null;
        return gameDate && gameDate >= oneMonthAgo;
      }).length;

      setAnalytics({
        totalGames,
        totalTeams,
        averageTeamsPerGame,
        averageGPAScore,
        totalRevenue,
        gamesThisMonth,
        gamesThisWeek
      });

    } catch (error) {
      console.error('Error fetching venue data:', error);
      setError('Failed to load venue data');
    } finally {
      setLoading(false);
    }
  }, [venueId]);

  useEffect(() => {
    if (!user || user.role !== 'admin') {
      navigate('/login');
      return;
    }
    if (venueId) {
      fetchVenueData();
    }
  }, [user, navigate, venueId, fetchVenueData]);

  const StatCard = ({ title, value, icon, color, subtitle }) => (
    <div className={`bg-gray-800 rounded-lg shadow-md p-6 border-l-4 ${color} border border-gray-700`}>
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-gray-300">{title}</p>
          <p className="text-2xl font-bold text-white">{value}</p>
          {subtitle && <p className="text-xs text-gray-400 mt-1">{subtitle}</p>}
        </div>
        <div className="text-3xl">{icon}</div>
      </div>
    </div>
  );

  const GameCard = ({ game }) => {
    const getGameStatus = () => {
      if (game.completed) return { text: 'Completed', color: 'bg-green-900 text-green-300' };
      if (game.inProgress) return { text: 'In Progress', color: 'bg-blue-900 text-blue-300' };
      return { text: 'Scheduled', color: 'bg-yellow-900 text-yellow-300' };
    };

    const status = getGameStatus();

    return (
      <div className="bg-gray-800 rounded-lg shadow-md p-4 border border-gray-700">
        <div className="flex justify-between items-start mb-3">
          <h4 className="text-lg font-semibold text-white">{game.title || 'Untitled Game'}</h4>
          <span className={`px-2 py-1 rounded-full text-xs font-medium ${status.color}`}>
            {status.text}
          </span>
        </div>
        
        <div className="space-y-2 text-sm text-gray-300">
          <p>📅 {game.date || 'No date'}</p>
          <p>👥 {game.teamCount || 0} teams</p>
          {game.hostName && <p>🎤 Host: {game.hostName}</p>}
          {game.finalResults && (
            <div className="mt-2 p-2 bg-gray-700 rounded">
              <p className="font-medium text-white">Final Results:</p>
              {game.finalResults.teams?.slice(0, 3).map((team, index) => (
                <p key={index} className="text-sm">
                  #{index + 1}: {team.name} - {team.score} pts
                  {team.gpa && ` (GPA: ${team.gpa})`}
                </p>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-400 mx-auto"></div>
          <p className="mt-4 text-gray-300">Loading venue profile...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="text-6xl mb-4">⚠️</div>
          <h2 className="text-2xl font-bold text-red-400 mb-4">{error}</h2>
          <button
            onClick={() => navigate('/admin')}
            className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            Back to Admin Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900">
      <Header />
      
      <div className="p-8">
        {/* Header */}
        <div className="flex justify-between items-start mb-8">
          <div>
            <h1 className="text-3xl font-bold text-white mb-2">🏢 {venue?.name}</h1>
            <p className="text-gray-300 mb-2">{venue?.address}</p>
            {venue?.chain && (
              <p className="text-gray-400 text-sm">Chain: {venue.chain}</p>
            )}
            <div className="flex items-center space-x-4 mt-2 text-sm text-gray-400">
              {venue?.city && <span>🏙️ {venue.city}</span>}
              {venue?.state && <span>🗺️ {venue.state}</span>}
              {venue?.country && <span>🌎 {venue.country}</span>}
            </div>
          </div>
          <button
            onClick={() => navigate('/admin')}
            className="bg-gray-700 text-white px-4 py-2 rounded-lg hover:bg-gray-600 transition-colors"
          >
            ← Back to Admin
          </button>
        </div>

        {/* Analytics Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <StatCard
            title="Total Games"
            value={analytics.totalGames}
            icon="🧠"
            color="border-blue-400"
            subtitle={`${analytics.gamesThisMonth} this month`}
          />
          <StatCard
            title="Total Teams"
            value={analytics.totalTeams}
            icon="🏆"
            color="border-green-400"
            subtitle={`${analytics.averageTeamsPerGame} avg per game`}
          />
          <StatCard
            title="Average GPA"
            value={analytics.averageGPAScore}
            icon="📊"
            color="border-purple-400"
            subtitle="Game Performance Average"
          />
          <StatCard
            title="Revenue"
            value={`$${analytics.totalRevenue.toLocaleString()}`}
            icon="💰"
            color="border-yellow-400"
            subtitle="From game scheduling"
          />
        </div>

        {/* Venue Details & Add-ons */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
          {/* Venue Details */}
          <div className="bg-gray-800 rounded-lg shadow-md p-6">
            <h2 className="text-xl font-semibold text-white mb-4">Venue Details</h2>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-gray-300">Venue ID</span>
                <span className="text-white font-mono text-sm">{venue?.id}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-300">Contact Email</span>
                <span className="text-white">{venue?.email || 'Not provided'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-300">Phone</span>
                <span className="text-white">{venue?.phone || 'Not provided'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-300">Website</span>
                <span className="text-white">{venue?.website || 'Not provided'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-300">Capacity</span>
                <span className="text-white">{venue?.capacity || 'Not specified'}</span>
              </div>
            </div>
          </div>

          {/* Add-ons & Features */}
          <div className="bg-gray-800 rounded-lg shadow-md p-6">
            <h2 className="text-xl font-semibold text-white mb-4">Add-ons & Features</h2>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-gray-300">Premium League Slots</span>
                <span className={`px-2 py-1 rounded text-xs font-medium ${
                  venue?.premiumLeagueSlots ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                }`}>
                  {venue?.premiumLeagueSlots ? 'Active' : 'Inactive'}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-300">Exclusive Ads</span>
                <span className={`px-2 py-1 rounded text-xs font-medium ${
                  venue?.exclusiveAds ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                }`}>
                  {venue?.exclusiveAds ? 'Active' : 'Inactive'}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-300">Custom Slides</span>
                <span className={`px-2 py-1 rounded text-xs font-medium ${
                  venue?.customSlides ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                }`}>
                  {venue?.customSlides ? 'Active' : 'Inactive'}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-300">Priority Support</span>
                <span className={`px-2 py-1 rounded text-xs font-medium ${
                  venue?.prioritySupport ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                }`}>
                  {venue?.prioritySupport ? 'Active' : 'Inactive'}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Game History */}
        <div className="bg-gray-800 rounded-lg shadow-md p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-semibold text-white">Game History</h2>
            <div className="text-sm text-gray-400">
              {games.length} total games
            </div>
          </div>
          
          {games.length === 0 ? (
            <div className="text-center py-8">
              <div className="text-4xl mb-4">🧠</div>
              <p className="text-gray-400">No games have been played at this venue yet.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {games.slice(0, 12).map((game) => (
                <GameCard key={game.id} game={game} />
              ))}
            </div>
          )}
          
          {games.length > 12 && (
            <div className="text-center mt-6">
              <button className="text-blue-400 hover:text-blue-300 transition-colors">
                View All {games.length} Games
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default VenueProfile; 